// src/components/files/FileToolbar.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import SortIcon from '@mui/icons-material/Sort';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StorageIcon from '@mui/icons-material/Storage';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import { ViewMode, SortBy } from '../../types/files';
import FolderDialog from './FolderDialog';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const FileToolbar: React.FC = () => {
  const { t } = useTranslation();
  const { state, setViewMode, setSort, deleteFolder, deleteDocument, uploadDocument, search, clearSearch } = useFileManager();
  const { viewMode, sortBy, sortOrder, selectedItems, section, uploading, searching, searchQuery: contextSearchQuery } = state;

  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(contextSearchQuery || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debounce search query (300ms)
  const debouncedSearchQuery = useDebounce(localSearchQuery, 300);

  // Trigger search when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery.length >= 2) {
      search(debouncedSearchQuery);
    } else if (debouncedSearchQuery.length === 0 && section === 'search') {
      clearSearch();
    }
  }, [debouncedSearchQuery, search, clearSearch, section]);

  // Sync local search query with context when clearing
  useEffect(() => {
    if (contextSearchQuery === '' && localSearchQuery !== '') {
      setLocalSearchQuery('');
    }
  }, [contextSearchQuery, localSearchQuery]);

  const handleClearSearch = useCallback(() => {
    setLocalSearchQuery('');
    if (section === 'search') {
      clearSearch();
    }
  }, [section, clearSearch]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        try {
          await uploadDocument(files[i]);
        } catch (error) {
          console.error('Upload failed:', error);
        }
      }
    }
    // Reset input to allow uploading the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode) {
      setViewMode(newMode);
    }
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (newSortBy: SortBy) => {
    if (newSortBy === sortBy) {
      // Toggle order
      setSort(sortBy, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(newSortBy, 'asc');
    }
    handleSortClose();
  };

  const handleDeleteSelected = async () => {
    for (const item of selectedItems) {
      if (item.type === 'folder') {
        await deleteFolder(item.id);
      } else {
        await deleteDocument(item.id);
      }
    }
  };

  const hasSelection = selectedItems.length > 0;
  const isMySpace = section === 'my-space';

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 3,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          bgcolor: 'background.paper',
        }}
      >
        {/* Left side - Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isMySpace && (
            <>
              <Button
                variant="contained"
                startIcon={<CreateNewFolderIcon />}
                onClick={() => setFolderDialogOpen(true)}
                size="small"
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {t('fileManager.toolbar.newFolder', 'Nouveau dossier')}
              </Button>

              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                component="label"
                size="small"
                disabled={uploading}
              >
                {t('fileManager.toolbar.upload', 'Importer')}
                <input
                  type="file"
                  hidden
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                />
              </Button>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            </>
          )}

          {/* Selection actions */}
          {hasSelection && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('fileManager.toolbar.selected', '{{count}} selectionne(s)', { count: selectedItems.length })}
              </Typography>

              <Tooltip title={t('fileManager.toolbar.delete', 'Supprimer')}>
                <IconButton
                  size="small"
                  onClick={handleDeleteSelected}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('fileManager.toolbar.move', 'Deplacer')}>
                <IconButton size="small" sx={{ color: 'grey.600' }}>
                  <DriveFileMoveIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            </>
          )}
        </Box>

        {/* Right side - View controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder={t('fileManager.toolbar.search', 'Rechercher...')}
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searching ? (
                    <CircularProgress size={18} sx={{ color: 'grey.400' }} />
                  ) : (
                    <SearchIcon sx={{ fontSize: 20, color: 'grey.400' }} />
                  )}
                </InputAdornment>
              ),
              endAdornment: localSearchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.5 }}>
                    <ClearIcon sx={{ fontSize: 18, color: 'grey.400' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: 260,
              '& .MuiOutlinedInput-root': {
                bgcolor: section === 'search' ? 'primary.50' : 'grey.50',
                '& fieldset': { borderColor: section === 'search' ? 'primary.200' : 'grey.200' },
                '&:hover fieldset': { borderColor: section === 'search' ? 'primary.300' : 'grey.300' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
              },
            }}
          />

          {/* Sort */}
          <Tooltip title={t('fileManager.toolbar.sort', 'Trier')}>
            <IconButton size="small" onClick={handleSortClick}>
              <SortIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* View mode */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{
              bgcolor: 'grey.100',
              '& .MuiToggleButton-root': {
                border: 'none',
                px: 1.5,
                '&.Mui-selected': {
                  bgcolor: 'background.paper',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                },
              },
            }}
          >
            <ToggleButton value="grid">
              <GridViewIcon sx={{ fontSize: 20 }} />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewListIcon sx={{ fontSize: 20 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Sort menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleSortSelect('name')}>
          <ListItemIcon>
            <SortByAlphaIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('fileManager.sort.name', 'Nom')}</ListItemText>
          {sortBy === 'name' && (
            sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
          )}
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect('date')}>
          <ListItemIcon>
            <CalendarTodayIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('fileManager.sort.date', 'Date')}</ListItemText>
          {sortBy === 'date' && (
            sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
          )}
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect('size')}>
          <ListItemIcon>
            <StorageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('fileManager.sort.size', 'Taille')}</ListItemText>
          {sortBy === 'size' && (
            sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
          )}
        </MenuItem>
      </Menu>

      {/* New folder dialog */}
      <FolderDialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        mode="create"
      />
    </>
  );
};

export default FileToolbar;
